import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import './index.css';
import './font.css';
import '@wicket/enrollkit-sdk-core/wicket.css';

import React, { Suspense, useEffect } from 'react';

// import reportWebVitals from './reportWebVitals';
import posthog from 'posthog-js';
import ReactDOM from 'react-dom/client';
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from 'react-router-dom';

import { SentryError } from '@/views/misc';
import { LoadingOverlay, MantineProvider } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import * as Sentry from '@sentry/react';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';

import App from './App';
import ContextErrorModal from './components/modals/ContextErrorModal';
import { getEnvironment, theme as getTheme } from './utils';
import classes from './index.module.css';

if (import.meta.env.VITE_SENTRY_ENABLED !== 'false') {
  Sentry.init({
    dsn: 'https://79c97ef69c967717b8d0e5437bd1032e@o188333.ingest.sentry.io/4506735676686336',
    environment: getEnvironment(),
    integrations: [
      // See docs for support of different versions of variation of react router
      // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      }),
      Sentry.replayIntegration()
    ]
  });
}

if (import.meta.env.VITE_POSTHOG_ENABLED !== 'false') {
  posthog.init('phc_J3FVxlcUolJNipYYzBOahP4LtUhHLM2pBohJ8usQ3Qs', {
    api_host: 'https://us.i.posthog.com',
    person_profiles: 'always'
  });
}

// Initialize LaunchDarkly provider (client is added to context later in SitesLayout)
let LDProvider: React.ComponentType<any>;

const initializeLDProvider = async () => {
  LDProvider = await asyncWithLDProvider({
    clientSideID: import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_ID ?? '65c2a16accc9860ed7be83cb',
    timeout: 5 // 5 second timeout (default)
  });
};

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

// should have a API ko generate colors
const brandColor = [
  '#B6D1CF',
  '#9CC8C4',
  '#80C3BD',
  '#63C3BA',
  '#42C9BD',
  '#43AEA4',
  '#46938C',
  '#467D78',
  '#446C68',
  '#009184'
];

const theme = getTheme(
  brandColor || [
    '#B6D1CF',
    '#9CC8C4',
    '#80C3BD',
    '#63C3BA',
    '#42C9BD',
    '#43AEA4',
    '#46938C',
    '#467D78',
    '#446C68',
    '#009184'
  ]
);

const queryClient = new QueryClient({
  queryCache: new QueryCache({}),
  mutationCache: new MutationCache({}),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 5000,
      retry: 0
    }
  }
});

const modals = {
  error: ContextErrorModal
};

// Initialize LD Provider before rendering
initializeLDProvider().finally(() => {
  root.render(
    <React.StrictMode>
      <LDProvider>
        <MantineProvider theme={theme}>
          <Suspense fallback={<LoadingOverlay visible overlayProps={{ blur: 2 }} />}>
            <BrowserRouter>
              <SentryError>
                <QueryClientProvider client={queryClient}>
                  <ModalsProvider modals={{ ...modals }}>
                    <Notifications
                      position="bottom-center"
                      limit={3}
                      zIndex={999999}
                      classNames={{
                        root: classes.root
                      }}
                    />
                    <App />
                  </ModalsProvider>
                </QueryClientProvider>
              </SentryError>
            </BrowserRouter>
          </Suspense>
        </MantineProvider>
      </LDProvider>
    </React.StrictMode>
  );
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
